// @services/stubs/StubPageCounterClient.ts
import type { TaskEitherTs } from "@ps-bridge/interop";
import * as E from "fp-ts/Either";
import { taskEitherFromRaw } from "@utils/effects/branded";
import type {
    PageCounterClient,
    PageCounterClientError,
    PageCounterRequest,
    PageCounterResult,
} from "@services/clients/PageCounterClient";
import { toPageCounterError } from "@services/clients/PageCounterClient";

export type StubPageCounterMode = "success" | "responseDecodeError" | "serviceUnavailable";

export const STUB_PAGE_COUNTER_MODE_KEY = "stub:page-counter:mode";

const readMode = (): StubPageCounterMode => {
    if (typeof window === "undefined") {
        return "success";
    }

    const value = window.localStorage.getItem(STUB_PAGE_COUNTER_MODE_KEY) ?? "success";
    return value === "responseDecodeError" || value === "serviceUnavailable" ? value : "success";
};

const failure = (mode: Exclude<StubPageCounterMode, "success">): PageCounterClientError => {
    switch (mode) {
        case "responseDecodeError":
            return toPageCounterError("Unable to decode page counter response.", "responseDecodeError");
        case "serviceUnavailable":
            return toPageCounterError("Page counter service is unavailable.", "serviceUnavailable");
    }
};

const successResult = (request: PageCounterRequest): PageCounterResult => ({
    documentId: request.documentId,
    filename: "page-counter-stub.pdf",
    mimeType: "application/pdf",
    billablePages: 12,
    unitPriceCents: 250,
    totalCostCents: 3000,
    currency: "USD",
    billingOutcome: "success",
    message: "Stub page count completed.",
    traceId: `stub-page-counter-trace-${request.documentId}`,
});

export class StubPageCounterClient implements PageCounterClient {
    countUploadedDocument(request: PageCounterRequest): TaskEitherTs<PageCounterClientError, PageCounterResult> {
        const mode = readMode();
        console.log("StubPageCounterClient.countUploadedDocument called", request, mode);

        if (mode !== "success") {
            return taskEitherFromRaw(async () => E.left(failure(mode)));
        }

        return taskEitherFromRaw(async () => E.right(successResult(request)));
    }
}
